import { useState, lazy, Suspense } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTenant } from '@/contexts/TenantProvider'
import {
  Bell,
  Plus,
  CalendarCheck,
  UserPlus,
  Scissors,
  Mail,
  Package,
  Gift,
  TrendingUp,
  Zap,
  Sparkles,
} from 'lucide-react'
import AdminPageFrame from '@/components/admin/AdminPageFrame'
import StatusBadge from '@/components/admin/StatusBadge'
import { useRebookingPredictions } from '@/hooks/use-rebooking-predictions'
import {
  EMPTY_DASHBOARD_DATA,
  formatDashboardMoney,
  formatDashboardTime12,
  useAdminDashboardData,
} from '@/hooks/use-admin-dashboard-data'

const BookingWizardModal = lazy(() => import('@/components/booking/BookingWizardModal'))

/* ================================================================
   AdminDashboard
   ================================================================ */
export default function AdminDashboard() {
  const { slug } = useParams<{ slug: string }>()
  const { tenant } = useTenant()
  const tenantId = tenant?.id
  const [wizardOpen, setWizardOpen] = useState(false)
  const { data: predictions } = useRebookingPredictions(tenantId)
  const { data: dashboard = EMPTY_DASHBOARD_DATA } = useAdminDashboardData(tenantId)

  const { metrics, todayBookings, feed, topServices } = dashboard

  return (
    <>
      <AdminPageFrame
        title="Dashboard"
        actions={
          <div className="flex items-center gap-3">
            <Link
              to={`/${slug}/admin/liquid-preview`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-brand-gold bg-brand-gold-dim border border-brand-gold-glow hover:bg-brand-gold-dim/80 transition-colors"
            >
              <Sparkles size={14} />
              Liquid Preview
            </Link>
            <button
              type="button"
              aria-label="Notifications"
              className="relative w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-hover text-text-secondary transition-colors"
            >
              <Bell size={16} />
              {metrics.pending > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-status-error-text rounded-full" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-origin-blue hover:bg-origin-blue-hover transition-colors"
            >
              <Plus size={14} />
              New Appointment
            </button>
          </div>
        }
      >
        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            label="Today's Bookings"
            value={metrics.todayCount}
            delta={metrics.pending > 0 ? `${metrics.pending} pending` : 'All confirmed'}
            deltaPositive={metrics.pending === 0}
            accentClass="border-origin-blue"
          />
          <StatCard
            label="Monthly Revenue"
            value={formatDashboardMoney(metrics.monthlyRevenue)}
            delta="This month"
            deltaPositive
            accentClass="border-text-secondary"
          />
          <StatCard
            label="Active Clients"
            value={metrics.activeClients}
            delta="Last 90 days"
            deltaPositive
            accentClass="border-origin-blue-hover"
          />
          <StatCard
            label="Avg Booking Value"
            value={
              metrics.avgBookingValue > 0 ? formatDashboardMoney(metrics.avgBookingValue) : '—'
            }
            delta={
              <span className="inline-flex items-center gap-0.5">
                <TrendingUp size={11} /> Upward trend
              </span>
            }
            deltaPositive
            accentClass="border-origin-blue/40"
          />
        </div>

        {/* ── Two-column layout (responsive) ── */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Left — main content */}
          <div className="flex-1 min-w-0 space-y-6">
            {/* Upcoming Appointments */}
            <section className="bg-surface rounded-xl shadow-sm overflow-hidden border border-border">
              <div className="px-6 py-4 border-b border-border flex justify-between items-center">
                <h4 className="font-semibold text-text-primary text-base">Upcoming Appointments</h4>
                <Link
                  to={`/${slug}/admin/bookings`}
                  className="text-origin-blue text-sm font-semibold hover:underline"
                >
                  View Schedule
                </Link>
              </div>
              <div className="overflow-x-auto">
                {todayBookings.length === 0 ? (
                  <div className="py-12 text-center text-sm text-text-tertiary">
                    No appointments scheduled today
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="bg-surface-hover text-text-secondary text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-3 font-semibold">Time</th>
                        <th className="px-6 py-3 font-semibold">Client</th>
                        <th className="px-6 py-3 font-semibold">Service</th>
                        <th className="px-6 py-3 font-semibold">Stylist</th>
                        <th className="px-6 py-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {todayBookings.map((booking) => (
                        <tr key={booking.id} className="hover:bg-surface-hover transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-text-primary">
                            {formatDashboardTime12(booking.scheduled_at)}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-text-primary">
                            {booking.client_name}
                          </td>
                          <td className="px-6 py-4 text-sm text-text-secondary">
                            {booking.service_name}
                          </td>
                          <td className="px-6 py-4 text-sm text-text-secondary">
                            {booking.staff_name}
                          </td>
                          <td className="px-6 py-4">
                            <StatusBadge status={booking.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Recent Activity */}
            <section className="bg-surface rounded-xl shadow-sm p-6 border border-border">
              <h4 className="font-semibold text-text-primary text-base mb-5">Recent Activity</h4>
              {feed.length === 0 ? (
                <p className="text-sm text-text-tertiary">No activity yet today</p>
              ) : (
                <div className="space-y-5">
                  {feed.map((item) => {
                    const initials = item.client_name
                      .split(' ')
                      .map((p) => p[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()
                    return (
                      <div key={item.id} className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-origin-blue/10 flex items-center justify-center text-origin-blue font-bold text-xs shrink-0">
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-text-secondary">
                            <span className="font-semibold text-text-primary">
                              {item.client_name}
                            </span>
                            {' — '}
                            {item.service_name}
                          </p>
                          <p className="text-xs text-text-tertiary mt-0.5">
                            {formatDashboardTime12(item.updated_at)}
                          </p>
                        </div>
                        <StatusBadge status={item.status} />
                      </div>
                    )
                  })}
                </div>
              )}
            </section>

            {/* Rebooking Predictions (if data exists) */}
            {predictions && predictions.predictions.length > 0 && (
              <section className="bg-surface rounded-xl shadow-sm overflow-hidden border border-border">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h4 className="font-semibold text-text-primary text-base">
                    Rebooking Predictions
                  </h4>
                  {predictions.summary.at_risk > 0 && (
                    <StatusBadge status="pending" className="text-[10px]" />
                  )}
                </div>
                <div className="divide-y divide-border">
                  {predictions.predictions.slice(0, 6).map((p) => (
                    <div key={p.client_email} className="flex items-center gap-4 px-6 py-3">
                      <div className="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center text-[10px] font-bold text-text-secondary shrink-0">
                        {p.client_name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {p.client_name}
                        </p>
                        <p className="text-xs text-text-tertiary mt-0.5">
                          Last visit {new Date(p.last_visit).toLocaleDateString()} · every ~
                          {p.avg_interval_days}d
                        </p>
                      </div>
                      <div className="text-right shrink-0 text-xs">
                        {p.is_at_risk ? (
                          <span className="text-status-warning-text font-medium">
                            {p.days_overdue}d overdue
                          </span>
                        ) : (
                          <span className="text-status-success-text font-medium">On track</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right — sidebar (stacks on mobile) */}
          <div className="w-full lg:w-72 lg:shrink-0 space-y-6">
            {/* Quick Actions */}
            <section className="bg-surface rounded-xl shadow-sm p-6 border border-border">
              <h4 className="font-semibold text-text-primary text-base mb-4">Quick Actions</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: CalendarCheck, label: 'New Booking', onClick: () => setWizardOpen(true) },
                  { icon: UserPlus, label: 'Add Client', href: `/${slug}/admin/clients` },
                  { icon: Scissors, label: 'Add Service', href: `/${slug}/admin/services` },
                  { icon: Package, label: 'Inventory', href: `/${slug}/admin/inventory` },
                  { icon: Gift, label: 'Marketplace', href: `/${slug}/admin/marketplace` },
                  { icon: Mail, label: 'Send Campaign', href: `/${slug}/admin/marketing` },
                ].map(({ icon: Icon, label, href, onClick }) =>
                  href ? (
                    <Link
                      key={label}
                      to={href}
                      className="flex flex-col items-center justify-center p-4 rounded-lg bg-surface-hover hover:bg-surface-active transition-colors group text-center"
                    >
                      <Icon
                        size={20}
                        className="text-origin-blue mb-2 group-hover:scale-110 transition-transform"
                      />
                      <span className="text-xs font-semibold text-text-secondary">{label}</span>
                    </Link>
                  ) : (
                    <button
                      key={label}
                      type="button"
                      onClick={onClick}
                      className="flex flex-col items-center justify-center p-4 rounded-lg bg-surface-hover hover:bg-surface-active transition-colors group text-center"
                    >
                      <Icon
                        size={20}
                        className="text-origin-blue mb-2 group-hover:scale-110 transition-transform"
                      />
                      <span className="text-xs font-semibold text-text-secondary">{label}</span>
                    </button>
                  )
                )}
              </div>
            </section>

            {/* Top Services */}
            <section className="bg-surface rounded-xl shadow-sm p-6 border border-border">
              <h4 className="font-semibold text-text-primary text-base mb-4">Top Services</h4>
              {topServices.length === 0 ? (
                <p className="text-sm text-text-tertiary">No data yet this month</p>
              ) : (
                <div className="space-y-3">
                  {topServices.map((svc, i) => (
                    <div key={svc.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span
                          className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                            i === 0
                              ? 'bg-origin-blue/10 text-origin-blue'
                              : 'bg-surface-hover text-text-secondary'
                          }`}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="text-sm font-medium text-text-primary truncate max-w-[120px]">
                          {svc.name}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-text-secondary">{svc.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Flash Openings */}
            <section className="rounded-xl p-6 shadow-sm text-white relative overflow-hidden bg-origin-blue">
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-1">
                  <Zap size={16} className="text-sky-200" />
                  <h4 className="font-bold text-base">Flash Openings</h4>
                </div>
                <p className="text-sky-100 text-sm mb-4">Open slots available today</p>
                <Link
                  to={`/${slug}/admin/flash-openings`}
                  className="block w-full text-center bg-white text-origin-blue py-2 rounded-lg font-bold text-sm hover:bg-sky-50 transition-colors"
                >
                  Manage
                </Link>
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-white/5 rounded-full blur-xl pointer-events-none" />
            </section>
          </div>
        </div>
      </AdminPageFrame>

      <Suspense fallback={null}>
        <BookingWizardModal open={wizardOpen} onClose={() => setWizardOpen(false)} />
      </Suspense>
    </>
  )
}

/* ── StatCard ── */
function StatCard({
  label,
  value,
  delta,
  deltaPositive,
  accentClass,
}: {
  label: string
  value: string | number
  delta: React.ReactNode
  deltaPositive: boolean
  accentClass: string
}) {
  return (
    <div
      className={`bg-surface rounded-xl p-5 shadow-sm border-l-4 border border-border ${accentClass}`}
    >
      <p className="text-text-secondary text-sm font-medium mb-1">{label}</p>
      <div className="flex items-end justify-between gap-2">
        <h3 className="text-3xl font-bold text-text-primary font-display leading-none">{value}</h3>
        <span
          className={`text-xs font-semibold px-2 py-1 rounded whitespace-nowrap ${
            deltaPositive
              ? 'bg-origin-blue/10 text-origin-blue'
              : 'bg-status-warning-bg text-status-warning-text'
          }`}
        >
          {delta}
        </span>
      </div>
    </div>
  )
}
